/**
 * @file ImagePreviewGrid.tsx
 *
 * Controlled grid of gallery image thumbnails for the product form, mirroring
 * the Website Builder v2 layout: the first image is featured and spans a 2x2
 * cell; the rest are square thumbnails labelled "# N". Each tile has a hover
 * "more actions" (`⋯`) button opening a contextual menu — select/remove as
 * featured, move up/down in order, and remove from selection — so all reordering
 * and feature changes flow through `onFilesChange`. In-flight uploads render as
 * loader placeholders.
 */

import { useState } from 'react';
import type { ComponentProps } from 'react';
import { Icon, Loader } from '@group.one/react-gravity';
import { ImageOrDefault } from './ImageOrDefault';
import styles from './ImagePreviewGrid.module.css';

type IconName = ComponentProps<typeof Icon>['name'];

interface ImagePreviewGridProps {
  files: string[];
  onFilesChange: (files: string[]) => void;
  uploadingCount: number;
  altText: string;
  uploadingLabel: string;
  featuredLabel: string;
  actionsLabel: string;
  closeLabel: string;
  setFeaturedLabel: string;
  removeFeaturedLabel: string;
  moveUpLabel: string;
  moveDownLabel: string;
  removeLabel: string;
}

interface MenuItem {
  value: string;
  label: string;
  icon: IconName;
}

/**
 * Renders the gallery thumbnails with a per-tile actions menu.
 *
 * @param props - The image URLs, change handler, in-flight count, and labels.
 *
 * @returns The preview grid element.
 */
export function ImagePreviewGrid({
  files,
  onFilesChange,
  uploadingCount,
  altText,
  uploadingLabel,
  featuredLabel,
  actionsLabel,
  closeLabel,
  setFeaturedLabel,
  removeFeaturedLabel,
  moveUpLabel,
  moveDownLabel,
  removeLabel,
}: ImagePreviewGridProps) {
  const [openMenuIndex, setOpenMenuIndex] = useState<number | null>(null);

  const buildMenuItems = (index: number): MenuItem[] => {
    const isFirst = index === 0;
    const isLast = index === files.length - 1;
    const isSingle = files.length === 1;
    const items: MenuItem[] = [];
    if (isFirst && !isSingle) {
      items.push({ value: 'remove_featured', label: removeFeaturedLabel, icon: 'bookmark_star' });
    } else if (!isFirst) {
      items.push({ value: 'set_featured', label: setFeaturedLabel, icon: 'bookmark_star' });
    }
    if (index > 1) {
      items.push({ value: 'move_up', label: moveUpLabel, icon: 'move_up' });
    }
    if (!isFirst && !isLast) {
      items.push({ value: 'move_down', label: moveDownLabel, icon: 'move_down' });
    }
    items.push({ value: 'remove', label: removeLabel, icon: 'cancel' });
    return items;
  };

  const applyAction = (value: string, index: number): void => {
    let next: string[] | null = null;
    switch (value) {
      case 'remove_featured':
        if (files.length >= 2) {
          next = [...files];
          const [demoted] = next.splice(0, 1);
          next.splice(1, 0, demoted);
        }
        break;
      case 'set_featured':
        if (index > 0) {
          next = [...files];
          const [promoted] = next.splice(index, 1);
          next.unshift(promoted);
        }
        break;
      case 'move_up':
        if (index > 0) {
          next = [...files];
          [next[index - 1], next[index]] = [next[index], next[index - 1]];
        }
        break;
      case 'move_down':
        if (index < files.length - 1) {
          next = [...files];
          [next[index], next[index + 1]] = [next[index + 1], next[index]];
        }
        break;
      case 'remove':
        next = files.filter((_, i) => i !== index);
        break;
    }
    if (next !== null) {
      onFilesChange(next);
    }
    setOpenMenuIndex(null);
  };

  return (
    <div className="gv-flex gv-flex-col gv-gap-sm">
      <div className={styles.grid}>
        {files.map((url, index) => {
          const isFeatured = index === 0;
          const thumbClassName = [
            styles.thumb,
            isFeatured ? styles.thumbFeatured : '',
            openMenuIndex === index ? styles.thumbMenuOpen : '',
          ]
            .filter(Boolean)
            .join(' ');

          return (
            <div
              key={`${url}-${index}`}
              className={thumbClassName}
              data-testid="product-form-media-thumb"
              data-index={index}
            >
              <ImageOrDefault src={url} alt={altText} className={styles.thumbImage} />
              <div
                role="button"
                tabIndex={0}
                className={styles.moreBtn}
                aria-label={actionsLabel}
                data-testid="product-form-media-actions"
                onClick={() =>
                  setOpenMenuIndex((current) => (current === index ? null : index))
                }
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setOpenMenuIndex((current) => (current === index ? null : index));
                  }
                }}
              >
                <Icon name="more_horiz" size="small" />
              </div>
              <div className={`${styles.badge} gv-caption-lg gv-radius gv-border`}>
                {isFeatured ? featuredLabel : `# ${index + 1}`}
              </div>
              {openMenuIndex === index && (
                <div className={`gv-contextual-menu ${styles.menu}`}>
                  <div className="gv-menu">
                    <button
                      type="button"
                      className="gv-btn-close"
                      aria-label={closeLabel}
                      onClick={() => setOpenMenuIndex(null)}
                    >
                      <Icon name="close" size="small" aria-hidden="true" />
                    </button>
                    <ul>
                      {buildMenuItems(index).map((item) => (
                        <li key={item.value}>
                          <button
                            type="button"
                            className="gv-menu-item"
                            data-testid={`product-form-media-action-${item.value}`}
                            onClick={() => applyAction(item.value, index)}
                          >
                            <Icon name={item.icon} size="small" aria-hidden="true" />
                            <span>{item.label}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {Array.from({ length: uploadingCount }).map((_, i) => (
          <div
            key={`uploading-${i}`}
            className={`${styles.thumb} ${styles.uploading} ${
              files.length === 0 && i === 0 ? styles.thumbFeatured : ''
            }`}
          >
            <Loader condensed>{uploadingLabel}</Loader>
          </div>
        ))}
      </div>

      {openMenuIndex !== null && (
        <div className={styles.menuOverlay} onMouseDown={() => setOpenMenuIndex(null)} />
      )}
    </div>
  );
}
